// src/components/InvoiceLineItems.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Grid, TextField, Typography, Tooltip, Icon } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Delete, Add, Save } from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { useLanguage } from "context/LanguageContext";

function InvoiceLineItems({ lineItems = [], onSave, discountAmount = 0, discountPercentage = 0 }) {
  const { t } = useLanguage();
  const [items, setItems] = useState(lineItems);
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const parseNumber = (value) => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = parseFloat(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const calculateLineAmount = (item) => {
    const quantity = parseNumber(item.quantity) || 0;
    const unitPrice = parseNumber(item.unit_price) || 0;
    return quantity * unitPrice;
  };

  const normalizeItem = (item) => {
    if (item.total_amount !== undefined && item.total_amount !== null && item.total_amount !== "") {
      return item;
    }
    return {
      ...item,
      total_amount: calculateLineAmount(item).toFixed(2),
    };
  };

  // Keep internal state in sync when parent updates lineItems
  useEffect(() => {
    setItems((lineItems || []).map(normalizeItem));
  }, [lineItems]);

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...items];
    const updatedItem = { ...newItems[index], [field]: value };

    if (field === "quantity" || field === "unit_price") {
      const quantity = parseNumber(updatedItem.quantity);
      const unitPrice = parseNumber(updatedItem.unit_price);
      if (quantity !== null && unitPrice !== null) {
        updatedItem.total_amount = (quantity * unitPrice).toFixed(2);
      }
    }

    if (field === "total_amount") {
      const quantity = parseNumber(updatedItem.quantity);
      const totalAmount = parseNumber(updatedItem.total_amount);
      if (quantity !== null && quantity !== 0 && totalAmount !== null) {
        updatedItem.unit_price = (totalAmount / quantity).toFixed(4);
      }
    }

    newItems[index] = updatedItem;
    setItems(newItems);
  };

  const handleAddLineItem = () => {
    setItems([
      ...items,
      {
        sku: "",
        description: "",
        quantity: 0,
        unit_price: 0,
        total_amount: "0.00",
        unit_of_measure: "",
      },
    ]);
  };

  const handleRemoveLineItem = (index) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave(items);
  };

  const getLineAmount = (item) => {
    const explicitTotal = parseNumber(item.total_amount);
    return explicitTotal !== null ? explicitTotal : calculateLineAmount(item);
  };

  const totalQuantity = items.reduce((acc, item) => acc + (parseNumber(item.quantity) || 0), 0);
  const totalAmount = items.reduce((acc, item) => acc + getLineAmount(item), 0);

  const validatedDiscountAmount = parseFloat(discountAmount) || 0;
  const validatedDiscountPercentage = parseFloat(discountPercentage) || 0;

  return (
    <Box>
      {/* Header row (hidden on mobile) */}
      {!isMdDown && (
        <Grid container spacing={2} alignItems="center" sx={{ minHeight: "24px", mb: 1 }}>
          <Grid item xs={1}>
            <Typography variant="subtitle1">#</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">{t("sku")}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">{t("description")}</Typography>
          </Grid>
          <Grid item xs={1.5}>
            <Typography variant="subtitle1">{t("quantity")}</Typography>
          </Grid>
          <Grid item xs={1.5}>
            <Typography variant="subtitle1">{t("unitPrice")}</Typography>
          </Grid>
          <Grid item xs={1.5}>
            <Typography variant="subtitle1">{t("lineAmount")}</Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="subtitle1">{t("unitOfMeasure")}</Typography>
          </Grid>
          <Grid item xs={0.5}></Grid>
        </Grid>
      )}

      <form>
        {items.map((item, index) =>
          isMdDown ? (
            <Box
              key={index}
              sx={{
                mb: 1.5,
                p: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t("lineItem")} #{index + 1}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    label={t("sku")}
                    value={item.sku}
                    onChange={(e) => handleLineItemChange(index, "sku", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 13 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("description")}
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("quantity")}
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ step: "0.001" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("unitPrice")}
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => handleLineItemChange(index, "unit_price", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ step: "0.0001" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("lineAmount")}
                    type="number"
                    value={item.total_amount ?? getLineAmount(item).toFixed(2)}
                    onChange={(e) => handleLineItemChange(index, "total_amount", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("unitOfMeasure")}
                    value={item.unit_of_measure}
                    onChange={(e) => handleLineItemChange(index, "unit_of_measure", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 5 }}
                  />
                </Grid>
              </Grid>
              <MDBox display="flex" justifyContent="flex-end" mt={1}>
                <MDButton variant="text" color="error" onClick={() => handleRemoveLineItem(index)}>
                  <Icon>delete</Icon>
                </MDButton>
              </MDBox>
            </Box>
          ) : (
            <Grid container spacing={0.3} key={index} alignItems="center" sx={{ mb: 0.1 }}>
              <Grid item xs={1}>
                <Typography variant="body2" sx={{ textAlign: "right", pr: 1 }}>
                  {index + 1}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  value={item.sku}
                  onChange={(e) => handleLineItemChange(index, "sku", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    maxLength: 13,
                    style: { fontSize: "0.75rem", padding: "5px 8px" },
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <Tooltip title={item.description}>
                  <TextField
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{
                      style: { fontSize: "0.75rem", padding: "5px 8px" },
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={1.5}>
                <TextField
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    style: { fontSize: "0.75rem", padding: "5px 8px" },
                    step: "0.001",
                  }}
                />
              </Grid>
              <Grid item xs={1.5}>
                <TextField
                  type="number"
                  value={item.unit_price}
                  onChange={(e) => handleLineItemChange(index, "unit_price", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    style: { fontSize: "0.75rem", padding: "5px 8px" },
                    step: "0.0001",
                  }}
                />
              </Grid>
              <Grid item xs={1.5}>
                <TextField
                  type="number"
                  value={item.total_amount ?? getLineAmount(item).toFixed(2)}
                  onChange={(e) => handleLineItemChange(index, "total_amount", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    style: { fontSize: "0.75rem", padding: "5px 8px" },
                    step: "0.01",
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  value={item.unit_of_measure}
                  onChange={(e) => handleLineItemChange(index, "unit_of_measure", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    maxLength: 5,
                    style: { fontSize: "0.75rem", padding: "5px 8px", width: "50px" },
                  }}
                />
              </Grid>
              <Grid item xs={0.5}>
                <MDBox mr={1}>
                  <MDButton
                    variant="text"
                    color="error"
                    onClick={() => handleRemoveLineItem(index)}
                  >
                    <Icon>delete</Icon>
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          )
        )}

        {/* Add Line Item Button */}
        <Grid container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
          <Grid item>
            <MDButton
              variant="text"
              color="secondary"
              onClick={handleAddLineItem}
              startIcon={<Add />}
            >
              {t("addLineItem")}
            </MDButton>
          </Grid>
          <Grid item>
            <MDButton variant="text" color="primary" onClick={handleSave} startIcon={<Save />}>
              {t("saveChanges")}
            </MDButton>
          </Grid>
        </Grid>
      </form>

      {/* Totals Row */}
      {isMdDown ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">
            {t("totalQuantity")}: {totalQuantity}
          </Typography>
          <Typography variant="subtitle1">
            {t("totalAmount")}: {totalAmount.toFixed(2)}
          </Typography>
          {validatedDiscountAmount > 0 && (
            <Typography variant="subtitle1">
              {t("discount")}: {validatedDiscountAmount.toFixed(2)} (
              {validatedDiscountPercentage.toFixed(2)}%)
            </Typography>
          )}
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={1}></Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">{t("total")}:</Typography>
          </Grid>
          <Grid item xs={3}></Grid>
          <Grid item xs={1.5}>
            <Typography variant="subtitle1">{totalQuantity}</Typography>
          </Grid>
          <Grid item xs={0.5}></Grid>
          <Grid item xs={2.5}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "right",
                pr: 1,
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {totalAmount.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={1.5}>
            {validatedDiscountAmount > 0 && (
              <Typography variant="subtitle1">
                {t("discount")}: {validatedDiscountAmount.toFixed(2)} (
                {validatedDiscountPercentage.toFixed(2)}%)
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

InvoiceLineItems.propTypes = {
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      description: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      unit_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      total_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      unit_of_measure: PropTypes.string,
    })
  ),
  onSave: PropTypes.func.isRequired,
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  discountPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default InvoiceLineItems;
